import { useState, useCallback, CSSProperties } from 'react';
import { Dropdown, Typography, theme, Button } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import ColumnFilter from './ColumnFilter';

const { Text } = Typography;

type DocListColumnMenuProps = {
  columnId: string;
  label: string;
  filterable: boolean;
  filterDisabled: boolean;
  filterValue: unknown;
  onFilterChange: (value: unknown) => void;
  onFilterClear: () => void;
  facetedUniqueValues: Map<string, number>;
  columnWidth: number;
  filterDropdownWidth?: number;
  getLabel?: (columnId: string, value: unknown) => string;
  actionItems: MenuProps['items'];
  disabled?: boolean;
  style?: CSSProperties;
  ariaLabel?: string;
  children: React.ReactNode;
};

function DocListColumnMenu({
  columnId,
  label,
  filterable,
  filterDisabled,
  filterValue,
  onFilterChange,
  onFilterClear,
  facetedUniqueValues,
  columnWidth,
  filterDropdownWidth,
  getLabel,
  actionItems,
  disabled = false,
  style,
  ariaLabel,
  children,
}: DocListColumnMenuProps) {
  const { token } = theme.useToken();
  const [open, setOpen] = useState(false);

  const columnFilterValue = Array.isArray(filterValue)
    ? filterValue.map((value) => String(value))
    : filterValue === null || filterValue === undefined || String(filterValue) === ''
      ? undefined
      : [String(filterValue)];

  const hasFilter = columnFilterValue !== undefined && columnFilterValue.length > 0;

  const handleClearFilter = useCallback(() => {
    onFilterClear();
  }, [onFilterClear]);

  const hasFilterableValues = Array.from(facetedUniqueValues.keys()).length > 0;

  const filterMenuItems: MenuProps['items'] = filterable && hasFilterableValues
    ? [
        {
          key: 'filter-header',
          type: 'group',
          label: (
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              Filter by {label}
            </Text>
          ),
        },
        {
          key: 'filter-select',
          label: (
            <div
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              style={{
                padding: `${token.paddingXXS}px 0`,
                opacity: filterDisabled ? 0.4 : 1,
                pointerEvents: filterDisabled ? 'none' : 'auto',
              }}
            >
              <ColumnFilter
                columnId={columnId}
                filterValue={filterValue}
                onFilterChange={onFilterChange}
                onFilterClear={onFilterClear}
                facetedUniqueValues={facetedUniqueValues}
                columnWidth={columnWidth}
                filterDropdownWidth={filterDropdownWidth}
                getLabel={getLabel}
                ariaLabel={`doclist-filter-select-${columnId}`}
              />
            </div>
          ),
          disabled: true,
        },
        ...(hasFilter && !filterDisabled
          ? [
              { type: 'divider' as const, key: 'filter-divider-clear' },
              {
                key: 'clear-filter',
                label: (
                  <Button
                    type="link"
                    size="small"
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={handleClearFilter}
                    style={{ padding: 0 }}
                  >
                    Clear filter
                  </Button>
                ),
              },
            ]
          : []),
      ]
    : [];

  const menuItems: MenuProps['items'] = [
    ...filterMenuItems,
    ...(filterMenuItems.length > 0 && actionItems && actionItems.length > 0
      ? [{ type: 'divider' as const, key: 'filter-actions-divider' }]
      : []),
    ...(actionItems ?? []),
  ];

  return (
    <Dropdown
      menu={{ items: menuItems }}
      trigger={['click']}
      placement="bottomLeft"
      disabled={disabled}
      open={open}
      onOpenChange={(visible) => {
        setOpen(visible);
      }}
    >
      <div
        role="columnheader"
        aria-label={ariaLabel}
        style={style}
      >
        {children}
      </div>
    </Dropdown>
  );
}

export default DocListColumnMenu;
